import { CardContent, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import moment from 'moment-timezone';
import { FC, useEffect, useMemo, useState } from 'react';
import { usePatientProfile } from '../../../../../../hooks/use-patient-profile';
import { useVisitsService } from '../../../../../../hooks/use-visits-service';
import {
  AppointmentStatus,
  AppointmentStatusText,
  PatientAppointment
} from '../../../../../../services/appointment/types';
import { ProceduresDialog } from '../../../../../layout/slideout/patient-profile-widget/views/visits/procedures-dialog/procedures-dialog';
import { getSpentChip } from '../../../../../layout/slideout/patient-profile-widget/views/visits/utils/get-spent-chip';
import { NoDataComponent } from '../../../../../shared/grid/no-data';
import { Card, CardTitle, Table } from '../../patient-profile-page.styles';
import { InsuranceComponent } from '../overview/insurance-component';
import VisitsLoadingSkeleton from './visits-skeleton';
import { ClickableDescription, Container, SpentChip, StatusText, Switch } from './visits.styles';

export const VisitsView: FC = () => {
  const { contactDetails, locationId } = usePatientProfile();

  const [selectedItem, setSelectedItem] = useState<PatientAppointment>();
  const [dialogOpened, setDialogOpened] = useState(false);

  const { isLoading, appointments, getMoreData, updateAppointmentStatus } = useVisitsService(
    contactDetails?.id,
    contactDetails?.location?.id
  );

  const visitsAfterToday = useMemo(() => {
    if (!appointments) return [];
    return appointments.filter((appointment) => moment(appointment.date).startOf('day') >= moment().startOf('day'));
  }, [appointments]);

  const visitsBeforeToday = useMemo(() => {
    if (!appointments) return [];
    return appointments.filter((appointment) => moment(appointment.date).endOf('day') < moment().endOf('day'));
  }, [appointments]);

  useEffect(() => {
    if (visitsAfterToday.length) {
      Promise.all(visitsAfterToday.map((visit) => getMoreData(visit)));
    }
  }, [visitsAfterToday]);

  const handleItemClick = (appointment: PatientAppointment) => {
    setSelectedItem(appointment);
    setDialogOpened(true);
  };

  const handleDialogClose = () => {
    setDialogOpened(false);
    setSelectedItem(undefined);
  };

  if (isLoading && appointments?.length == 0) {
    return <VisitsLoadingSkeleton />;
  }

  return (
    <Container>
      <Card>
        <CardContent>
          <CardTitle>
            <span>
              {contactDetails?.firstName} {contactDetails?.lastName}’s Scheduled Visits
            </span>
          </CardTitle>
          {visitsAfterToday?.length === 0 && <NoDataComponent />}
          {!!visitsAfterToday?.length && (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell style={{ width: '15%' }}>Date</TableCell>
                  <TableCell>Provider</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Insurance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visitsAfterToday?.map((visit) => (
                  <TableRow key={`visit-${visit.id}`}>
                    <TableCell style={{ width: '15%' }}>
                      {visit.date ? moment(visit.date).format('MM/DD/YYYY') : null}
                    </TableCell>
                    <TableCell>{visit.moreData?.providerName}</TableCell>
                    <TableCell>
                      {visit.status !== AppointmentStatus.Cancelled && (
                        <Switch
                          checked={visit.status === 1}
                          onChange={(e) => {
                            updateAppointmentStatus(
                              visit,
                              e.target.checked ? AppointmentStatus.Confirmed : AppointmentStatus.Unconfirmed
                            );
                          }}
                        />
                      )}
                      <StatusText
                        status={visit.status}
                        text={AppointmentStatusText[visit.status as AppointmentStatus]}
                      />
                    </TableCell>
                    <TableCell>
                      <InsuranceComponent type="primary" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <CardTitle>
            <span>
              {contactDetails?.firstName} {contactDetails?.lastName}’s Visit History
            </span>
          </CardTitle>
          {visitsBeforeToday?.length === 0 && <NoDataComponent />}
          {!!visitsBeforeToday?.length && (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell style={{ width: '15%' }}>Date</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell style={{ width: '15%' }}>Revenue</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visitsBeforeToday?.map((visit) => (
                  <TableRow key={`visit-${visit.id}`}>
                    <TableCell style={{ width: '10%' }}>
                      {visit.date ? moment(visit.date).format('MM/DD/YYYY') : null}
                    </TableCell>
                    <TableCell>
                      <ClickableDescription onClick={() => handleItemClick(visit)}>
                        {visit.description || '-'}
                      </ClickableDescription>
                    </TableCell>
                    <TableCell style={{ width: '10%' }}>
                      <SpentChip>{getSpentChip(visit)}</SpentChip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      {selectedItem && (
        <ProceduresDialog isOpen={dialogOpened} appointment={selectedItem} onClose={handleDialogClose} />
      )}
    </Container>
  );
};
